import React, { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Button, Card, Select, Space, Tag, Typography } from "antd";
import { Draggable } from "react-beautiful-dnd";
import { TbBrandWechat } from "react-icons/tb";
import { MdChecklist } from "react-icons/md";
import { RiUserLine } from "react-icons/ri";
import { IoDocumentTextOutline } from "react-icons/io5";
import { useTranslation } from "react-i18next";
import ProgressStep from "../progressSteps/ProgressSteps";
import "./KanbanCard.css";
import TransportOrderDetailModal from "../transportOrderDetailModal/TransportOrderDetailModal";
import NotificationBadge from "../notificationBadge/notificationBadge";
import IncidentModal from "../incidentList/incidentModal";
import { useChat } from "../../context/ChatContext";
import { DriverContext } from "../../context/DriverContext";

const TRANSPORT_TYPES = {
  CONTAINER_OUTBOUND: { label: "orderType.containerOutbound", color: "blue" },
  CONTAINER_INBOUND: { label: "orderType.containerInbound", color: "cyan" },
  CONVENTIONAL_PICKUP: { label: "orderType.conventionalPickup", color: "default" },
  CONVENTIONAL_DELIVERY: { label: "orderType.conventionalDelivery", color: "default" },
};

const KanbanCard = ({
  item,
  index,
  drivers,
  onAssignDriver,
  isOrderDetailOpen,
  onOpenOrderDetail,
  onCloseOrderDetail,
}) => {
  const { getFleetID } = useContext(AuthContext);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const { t } = useTranslation();
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [isIncidentModalOpen, setIncidentModalOpen] = useState(false);
  const [profilePic, setProfilePic] = useState(null);
  const [driverName, setDriverName] = useState(null);
  const [driverStatus, setDriverStatus] = useState(null);

  const { openChat } = useChat();
  const { driversDetailsById } = useContext(DriverContext);

  const assignedDriver = driversDetailsById[item.raw?.userId] || drivers.find((driver) => driver.id === item.raw?.userId);
  const transportType = TRANSPORT_TYPES[item.raw?.type || item.raw?.truckflow?.type];

  useEffect(() => {
    if (!assignedDriver) {
      setProfilePic(null);
      setDriverName(null);
      setDriverStatus(null);
      return;
    }
    setProfilePic(assignedDriver.profilePic || null);
    setDriverName(assignedDriver.name || null);
    setDriverStatus(assignedDriver.isOnline ? "online" : "offline");
  }, [assignedDriver]);

  const handleAssignDriver = async () => {
    if (!selectedDriver) return;
    if (typeof onAssignDriver !== "function") return;
    const success = await onAssignDriver(item, selectedDriver);
    if (success) {
      setSelectedDriver(null);
    }
  };
  const openPdf = (url) => {
    window.open(url, "_blank");
  };

  const handleOpenDetailModal = () => {
    onOpenOrderDetail?.(item.id);
  };

  const handleCloseDetailModal = () => {
    onCloseOrderDetail?.();
  };

  return (
    <Draggable key={item.id} draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className="kanban-card-wrapper"
          style={{
            ...provided.draggableProps.style,
            width: "100%",
            maxWidth: "360px",
            boxSizing: "border-box",
          }}
        >
          <Card
            size="small"
            className="kanban-card kanban-card--clickable"
            onClick={() => {
              if (snapshot.isDragging) return;
              handleOpenDetailModal();
            }}
          >
            <div className="kanban-card-header">
              <div className="kanban-card-meta">
                <div className="card-title">
                  <MdChecklist className="icon" />
                  <Typography.Text className="card-title-text">{item.ref}</Typography.Text>
                </div>
                {transportType && (
                  <Tag color={transportType.color} style={{ justifySelf: "start", whiteSpace: "normal", marginInlineEnd: 0 }}>
                    {t(transportType.label)}
                  </Tag>
                )}
                {item.raw?.customerReference || item.raw?.transportOrderRef ? (
                  <Typography.Text type="secondary">
                    {t("orderDetail.customerReference")}: {item.raw?.customerReference || item.raw?.transportOrderRef}
                  </Typography.Text>
                ) : null}
                <div className="card-title">
                  {item.status !== "PENDING" && <RiUserLine className="icon" />}
                  <Typography.Text className="card-subtitle">
                    {assignedDriver?.name}
                  </Typography.Text>
                </div>
                {["ACTIVE", "FINALIZED"].includes(item.status) && item.raw?.truckflow?.state && (
                  <Typography.Text type="secondary">
                    {t("transportCard.truckflow", { defaultValue: "Truckflow" })}: {item.raw.truckflow.state}
                  </Typography.Text>
                )}
                <Typography.Text type="secondary">{item.registered_fleet}</Typography.Text>
              </div>
              <Space
                size={6}
                align="center"
                wrap
                className="kanban-card-header-actions"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                {["FINALIZED", "ACTIVE"].includes(item.status) && (
                  <>
                    <NotificationBadge
                      fleetId={getFleetID()}
                      transportOrderId={item.id}
                      type="incident"
                      onClick={() => {
                        setSelectedOrderId(item.id);
                        setIncidentModalOpen(true);
                      }}
                      badgeOffset={{ top: "-7.5px", right: "-5px" }}
                    />
                    <Button
                      type="primary"
                      size="small"
                      icon={<TbBrandWechat size={16} />}
                      onClick={() =>
                        openChat({
                          key: `TO-${item.id}-${item.raw.userId}-${item.transportOrderId || "none"}`,
                          id: item.raw.userId,
                          name: `${item.ref} - ${driverName}`,
                          driverStatus: driverStatus,
                          type: "TO",
                          currentOrder: item.id,
                          transportOrderId: item.id,
                          profilePic: profilePic,
                        })
                      }
                    />
                  </>
                )}
                {item.raw?.viewPdfUrl && item.raw.viewPdfUrl.trim() !== "" && (
                  <Button
                    type="primary"
                    size="small"
                    icon={<IoDocumentTextOutline size={14} />}
                    onClick={() => openPdf(item.raw?.viewPdfUrl)}
                  />
                )}
                <TransportOrderDetailModal
                  isOpen={Boolean(isOrderDetailOpen)}
                  onClose={handleCloseDetailModal}
                  transportOrderId={item.id}
                  item={item}
                  driverName={driverName}
                />
              </Space>
            </div>

            {item.status === "PENDING" && (
              <div
                className="kanban-card-assign"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Select
                  placeholder={t("transportCard.driver")}
                  value={selectedDriver || undefined}
                  onChange={(value) => setSelectedDriver(value)}
                  options={drivers.map((driver) => ({
                    label:
                      driver.disabled && driver.disabledReason
                        ? `${driver.name} - ${driver.disabledReason}`
                        : driver.name,
                    value: driver.id,
                    disabled: driver.disabled,
                  }))}
                  showSearch
                  optionFilterProp="label"
                />
                <Button type="primary" disabled={!selectedDriver} onClick={handleAssignDriver}>
                  {t("transportCard.assignDriver")}
                </Button>
              </div>
            )}

            <div className="kanban-card-progress">
              {["ACTIVE", "FINALIZED"].includes(item.status) && (
                <ProgressStep
                  records={item.raw.truckflow?.transitions || item.raw.truckflow?.transitionRecords || []}
                  anomalies={item.raw.truckflow?.anomalies || item.raw.truckflow?.transitionAnomalies || []}
                  type={item.raw.truckflow?.type}
                  currentState={item.raw.truckflow?.state}
                />
              )}
            </div>

            <IncidentModal
              isOpen={isIncidentModalOpen}
              onClose={() => setIncidentModalOpen(false)}
              transportOrderId={selectedOrderId}
              transportOrderRef={item.ref || item.raw?.transportOrderRef || ""}
              fleetId={getFleetID()}
              driver={assignedDriver?.name || driverName || ""}
            />
          </Card>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
